import styled from 'styled-components';

import {
  FaChevronUp,
  FaChevronDown,
  FaBolt,
  FaCircleExclamation,
  FaScrewdriverWrench,
} from 'react-icons/fa6';

import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';

const StyledSidebar = styled.div`
  background: rgb(238, 238, 238);
  height: calc(100vh - 90px);
  position: fixed;
  top: 90px;
  left: 0;
  width: 200px;
  z-index: 0;
  overflow-y: auto;
  overflow-x: hidden;
`;

const StyledMenu = styled.ul`
  margin: 0 !important;
  padding: 0 !important;
`;

const StyledMenuItem = styled.li`
  background: rgb(238, 238, 238);
  list-style-type: none !important;
`;

const StyledMenuTrigger = styled.button`
  ${({ isExpanded, isActive }) => `
  align-items: center;
  border: none;
  background: none;
  font-weight: bold;
  color: rgb(153, 153, 153) !important;
  display: flex;
  padding: 10px 20px;
  text-decoration: none !important;
  transition: color .1s ease-out;
  width: 100%;
  justify-content: space-between;
  font-size: 14px;
  text-align: left;

  &:hover {
    color: #262626 !important;
  }
    
  & span {
    margin: 0;
    text-align: left;
  }

  ${
    isExpanded
      ? `
    font-weight: 600;
  `
      : ''
  }
  ${
    isActive
      ? `
    color: #262626 !important;
  `
      : ''
  }
`}
`;

const StyledMenuChildren = styled.ul`
  background: rgb(238, 238, 238);
`;

const StyledMenuLink = styled(Link)`
  align-items: center;
  border: none;
  background: none;
  font-weight: bold;
  color: rgb(153, 153, 153) !important;
  display: flex;
  padding: 8px 0 8px 30px;
  text-decoration: none !important;
  transition: color 0.1s ease-out;
  width: 100%;
  font-size: 13px;
  text-align: left;

  & span {
    margin: 0;
    text-align: left;
  }

  &:hover {
    color: #262626 !important;
  }
  ${({ isActive }) =>
    isActive
      ? `
    color: #262626 !important;
  `
      : ''}
`;

const StyledTopLevelMenuLink = styled(Link)`
  align-items: center;
  border: none;
  background: none;
  font-weight: bold;
  color: rgb(153, 153, 153) !important;
  display: flex;
  padding: 10px 20px;
  text-decoration: none !important;
  transition: color 0.1s ease-out;
  width: 100%;
  justify-content: space-between;
  font-size: 14px;
  text-align: left;

  &:hover {
    color: #262626 !important;
  }

  & span {
    margin: 0;
    text-align: left;
  }
  ${({ isActive }) =>
    isActive
      ? `
    color: #262626 !important;
  `
      : ''}
`;

const StyledChevron = styled.span`
  margin: 0;
  font-size: 12px;
`;

const ExpandableMenu = ({ label, icon, items, to, isParentExpanded, currentPath }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasChildren = Array.isArray(items) && items.length > 0;
  const isDirectLink = to && !hasChildren;

  const isActive = isDirectLink
    ? currentPath.startsWith(to)
    : hasChildren
    ? items.some((child) => currentPath.startsWith(child.to))
    : false;

  useEffect(() => {
    setIsExpanded(isParentExpanded || false);
  }, [isParentExpanded]);

  // If this is a direct link (has 'to' prop), render as a link but with same styling as trigger
  if (to && !items) {
    return (
      <StyledMenuItem>
        <StyledTopLevelMenuLink to={to} isActive={isActive}>
          <span>{label}</span>
          <StyledChevron style={{ visibility: 'hidden' }}>
            <FaChevronDown />
          </StyledChevron>
        </StyledTopLevelMenuLink>
      </StyledMenuItem>
    );
  }

  return (
    <>
      <StyledMenuItem
        onClick={() => {
          setIsExpanded(!isExpanded);
        }}
      >
        <StyledMenuTrigger isExpanded={isExpanded} isActive={isActive}>
          <span>{label}</span>

          {items && (
            <StyledChevron>
              {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
            </StyledChevron>
          )}
        </StyledMenuTrigger>
      </StyledMenuItem>

      {isExpanded && (
        <StyledMenuChildren>
          {items?.map(({ label, icon, to, items }) => {
            if (items) {
              return (
                <ExpandableMenu
                  key={label}
                  label={label}
                  icon={icon}
                  items={items}
                  isParentExpanded={isExpanded}
                />
              );
            }
            const childIsActive = currentPath.startsWith(to);

            return (
              <StyledMenuItem key={label}>
                <StyledMenuLink to={to} isActive={childIsActive}>
                  <span>{label}</span>
                </StyledMenuLink>
              </StyledMenuItem>
            );
          })}
        </StyledMenuChildren>
      )}
    </>
  );
};

export const Sidebar = () => {
  const location = useLocation();
  const navItems = [
    {
      label: 'Main Cybersecurity News',
      icon: <FaBolt />,
      items: [
        { label: 'News', icon: <FaBolt />, to: '/global/news' },
        { label: 'Podcasts', icon: <FaBolt />, to: '/global/podcasts' },
      ],
    },
    {
      label: 'Tech Stack Monitor',
      icon: <FaScrewdriverWrench />,
      items: [
        { label: 'News', icon: <FaBolt />, to: '/stack/news' },
        { label: 'Podcasts', icon: <FaBolt />, to: '/stack/podcasts' },
        { label: 'CVEs', icon: <FaCircleExclamation />, to: '/stack/cve' },
      ],
    },
    {
      label: 'Tools Monitor',
      icon: <FaScrewdriverWrench />,
      items: [
        { label: 'News', icon: <FaBolt />, to: '/tools/news' },
        { label: 'Podcasts', icon: <FaBolt />, to: '/tools/podcasts' },
        { label: 'CVEs', icon: <FaCircleExclamation />, to: '/tools/cve' },
      ],
    },
    {
      label: 'Mitre ATT&CK',
      icon: <FaCircleExclamation />,
      to: '/mitre',
    },
    {
      label: 'Integrations',
      icon: <FaBolt />,
      items: [
        { label: 'Slack', icon: <FaBolt />, to: '/slack' },
        { label: 'MS Teams', icon: <FaBolt />, to: '/msteams' },
        { label: 'Configuration', icon: <FaBolt />, to: '/config' },
      ],
    },
    {
      label: 'Notifications',
      icon: <FaBolt />,
      to: '/notifications',
    },
    {
      label: 'Manage/Edit Feeds',
      to: '/profile/edit',
    },
  ];

  return (
    <StyledSidebar>
      <StyledMenu>
        {navItems.map((props, index) => (
          <ExpandableMenu key={index} {...props} currentPath={location.pathname} />
        ))}
      </StyledMenu>
    </StyledSidebar>
  );
};
